import {
  AccountBalanceOutlined, AltRouteOutlined, AutoAwesomeOutlined, BedOutlined, BrushOutlined, CalculateOutlined, CategoryOutlined,
  DescriptionOutlined, DirectionsCarOutlined, EmailOutlined, HistoryEduOutlined, HotelOutlined, HubOutlined, KayakingOutlined,
  ListAltOutlined, MapOutlined, NumbersOutlined, PercentOutlined, PlaceOutlined, ReceiptLongOutlined, RouteOutlined,
  StorefrontOutlined, SupportAgentOutlined, TourOutlined, VerifiedUserOutlined, WhatsApp, AccountBalanceWalletOutlined,
  TravelExploreOutlined, HotelClassOutlined, DashboardCustomizeOutlined,
} from '@mui/icons-material';
import type { ReactNode } from 'react';

/**
 * FR-NAV-04/05/06: the Masters and Settings hubs — one page each of grouped tiles with a search box. Only tiles for screens that
 * exist are listed (FR-PLT-05: what a company cannot use is hidden, not greyed); each tile carries the same permission and
 * feature keys the screen's route uses, so the hub never offers a page that would refuse the person.
 */
export interface HubTile {
  /** Matches the record-count key from api/masters/summary (Masters hub only). */
  key: string;
  label: string;
  description: string;
  to: string;
  icon: ReactNode;
  perms?: string[];
  feature?: string;
}

export interface HubGroup {
  label: string;
  tiles: HubTile[];
}

const PLACES = { perms: ['City.View'], feature: 'Module.Destinations' };
const VENDORS = { perms: ['Vendor.View'], feature: 'Module.Vendors' };
const HOTELS = { perms: ['Hotel.View'], feature: 'Module.Hotels' };
const TRANSPORT = { perms: ['Transport.View'], feature: 'Module.Transport' };
const COMPANY = { perms: ['Company.View'] };

/** FR-NAV-05 Masters hub groups, in the SRS order. */
export const MASTERS_HUB: HubGroup[] = [
  {
    label: 'Places',
    tiles: [
      { key: 'destinations', label: 'Destinations', description: 'Cities, towns and villages tours are built from', to: '/destinations', icon: <PlaceOutlined />, ...PLACES },
      { key: 'attractions', label: 'Attractions', description: 'Tourist places with timings and entry fees', to: '/attractions', icon: <AccountBalanceOutlined />, ...PLACES },
      { key: 'activities', label: 'Activities', description: 'Rafting, paragliding and other experiences', to: '/activities', icon: <KayakingOutlined />, ...PLACES },
      { key: 'sub-tours', label: 'Sub-tours', description: 'City tours, excursions and en-route sightseeing', to: '/sub-tours', icon: <TourOutlined />, ...PLACES },
      { key: 'travel-times', label: 'Travel times', description: 'Distance and drive time between places', to: '/travel-times', icon: <AltRouteOutlined />, ...PLACES },
    ],
  },
  {
    label: 'Suppliers',
    tiles: [
      { key: 'vendors', label: 'Vendors', description: 'Hotels, transporters, DMCs and other suppliers', to: '/vendors', icon: <StorefrontOutlined />, ...VENDORS },
      { key: 'hotels', label: 'Hotels', description: 'Hotel profiles, rooms and their contracts', to: '/hotels', icon: <HotelOutlined />, ...HOTELS },
      { key: 'rate-card-types', label: 'Rate card types', description: 'How each region is priced for transport', to: '/rate-card-types', icon: <RouteOutlined />, perms: ['Transport.View', 'Transport.Setup'], feature: 'Module.Transport' },
    ],
  },
  {
    label: 'Rates and contracts',
    tiles: [
      { key: 'transport-rate-cards', label: 'Transport rate cards', description: "Transporters' approved and pending cards", to: '/transport-rate-cards', icon: <ReceiptLongOutlined />, ...TRANSPORT },
      { key: 'transport-mapping', label: 'Transport mapping', description: 'Which card the auto quote uses per destination', to: '/transport-mapping', icon: <HubOutlined />, ...TRANSPORT },
      { key: 'transport-calculator', label: 'Transport calculator', description: 'Price a trip on every approved card', to: '/transport-calculator', icon: <CalculateOutlined />, ...TRANSPORT },
      { key: 'hotel-calculator', label: 'Hotel rate calculator', description: 'Room-wise, night-by-night hotel cost', to: '/hotels/calculator', icon: <HotelClassOutlined />, ...HOTELS },
    ],
  },
  {
    label: 'Tour products',
    tiles: [
      { key: 'tour-categories', label: 'Tour categories', description: 'Domestic, international and regional groups', to: '/tour-categories', icon: <CategoryOutlined />, ...COMPANY },
    ],
  },
  {
    label: 'Sales lists',
    tiles: [
      { key: 'vehicle-types', label: 'Vehicle types', description: 'One vehicle list with seats and bags', to: '/vehicle-types', icon: <DirectionsCarOutlined />, perms: ['Transport.View', 'Transport.Setup'], feature: 'Module.Transport' },
      { key: 'hotel-lists', label: 'Hotel lists', description: 'Meal plans, room categories, amenities', to: '/hotel-lists', icon: <BedOutlined />, ...HOTELS },
      { key: 'vendor-lists', label: 'Vendor lists', description: 'Supply types, payment terms and other dropdowns', to: '/vendor-lists', icon: <ListAltOutlined />, ...VENDORS },
    ],
  },
  {
    label: 'Accounting lists',
    tiles: [
      { key: 'tds-sections', label: 'TDS sections', description: 'Sections, rates and thresholds for vendor payments', to: '/vendor-lists/tds', icon: <PercentOutlined />, ...VENDORS },
    ],
  },
];

/** FR-NAV-06 Settings hub groups (the settings that exist today). */
export const SETTINGS_HUB: HubGroup[] = [
  {
    label: 'Numbering and invoices',
    tiles: [
      { key: 'invoice-series', label: 'Numbering series', description: 'Invoice, credit note and debit note series', to: '/settings/invoice-series', icon: <NumbersOutlined />, ...COMPANY },
      { key: 'bank-accounts', label: 'Bank accounts', description: 'Accounts printed on invoices and quotes', to: '/settings/bank-accounts', icon: <AccountBalanceWalletOutlined />, ...COMPANY },
    ],
  },
  {
    label: 'PDF and document designs',
    tiles: [
      { key: 'branding', label: 'Branding', description: 'Logo, colours and how documents look', to: '/settings/branding', icon: <BrushOutlined />, ...COMPANY },
      { key: 'documents', label: 'Document templates', description: 'Quote, voucher and invoice templates', to: '/settings/documents', icon: <DescriptionOutlined />, ...COMPANY },
      { key: 'vendor-agreements', label: 'Vendor agreements', description: 'Agreement text vendors sign with OTP', to: '/settings/vendor-agreements', icon: <HistoryEduOutlined />, ...VENDORS },
    ],
  },
  {
    label: 'Integrations',
    tiles: [
      { key: 'email', label: 'Email', description: 'Sending address and test message', to: '/settings/email', icon: <EmailOutlined />, ...COMPANY },
      { key: 'whatsapp', label: 'WhatsApp', description: 'WhatsApp Business connection', to: '/settings/whatsapp', icon: <WhatsApp />, ...COMPANY },
      { key: 'ai', label: 'AI setup', description: 'AI provider and usage', to: '/settings/ai/configuration', icon: <AutoAwesomeOutlined />, ...COMPANY, feature: 'Module.Ai' },
    ],
  },
  {
    label: 'Places and regions',
    tiles: [
      { key: 'regions', label: 'Operating regions', description: 'States and countries the company sells', to: '/settings/regions', icon: <MapOutlined />, ...COMPANY },
      { key: 'places', label: 'Places defaults', description: 'Defaults used when places are added', to: '/settings/places', icon: <TravelExploreOutlined />, ...COMPANY },
    ],
  },
  {
    label: 'Access and compliance',
    tiles: [
      { key: 'kyc', label: 'Company KYC', description: 'KYC needed for invoices, payment links and invites', to: '/settings/kyc', icon: <VerifiedUserOutlined />, ...COMPANY },
      { key: 'support', label: 'Support access', description: 'Allow the TravelERP team to sign in to help', to: '/settings/support', icon: <SupportAgentOutlined />, ...COMPANY },
    ],
  },
  {
    label: 'Home and menus',
    tiles: [
      { key: 'home-defaults', label: 'Home and menus by role', description: 'Home cards and daily menu items each role starts with', to: '/settings/home-defaults', icon: <DashboardCustomizeOutlined />, perms: ['Role.View'] },
    ],
  },
];

const allTiles = (groups: HubGroup[]) => groups.flatMap((g) => g.tiles);

/** Every hub tile with the hub and group it sits in: what Home shortcuts and role pins are picked from (FR-NAV-03/07). */
export interface HubTileRef {
  hub: 'Masters' | 'Settings';
  group: string;
  tile: HubTile;
}

export const ALL_HUB_TILES: HubTileRef[] = [
  ...MASTERS_HUB.flatMap((g) => g.tiles.map((tile) => ({ hub: 'Masters' as const, group: g.label, tile }))),
  ...SETTINGS_HUB.flatMap((g) => g.tiles.map((tile) => ({ hub: 'Settings' as const, group: g.label, tile }))),
];

const TILE_BY_KEY = new Map(ALL_HUB_TILES.map((r) => [r.tile.key, r.tile]));

/** The hub tile for a menu or shortcut key; undefined for a key this build does not know. */
export const findHubTile = (key: string) => TILE_BY_KEY.get(key);

/** Every permission a hub's tiles use: the sidebar entry shows when the person may open at least one tile. */
export const hubPerms = (groups: HubGroup[]) => Array.from(new Set(allTiles(groups).flatMap((t) => t.perms ?? [])));

/** The routes a hub covers, so its sidebar entry reads as active on any of its pages. */
export const hubPaths = (groups: HubGroup[]) => allTiles(groups).map((t) => t.to);
